import {
	ChangeDetectionStrategy,
	Component,
	HostBinding,
	ViewEncapsulation,
	computed,
	effect,
	input,
	signal
} from '@angular/core';
import { parseJson, stringifyJson } from 'lib/helpers/json.utils.js';
import { match } from 'ts-pattern';
import { IButton } from '../buttons/index.js';
import { CoreComponent } from '../core/core.component.js';
import { IconColor, IconType } from '../icons/index.js';
import { IImage } from '../ui-models.js';

export type MessageType = 'error' | 'info' | 'success' | 'warning' | 'noData' | 'noDataOnBody' | 'tip';

type MessageDesign = 'combined' | 'standalone';

type DismissValue = '1';

type DismissedMessages = {
	[id: string]: DismissValue;
};

@Component({
	selector: 'lib-message',
	templateUrl: './message.component.html',
	styleUrls: ['./message.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	standalone: false
})
export class MessageComponent extends CoreComponent {
	public readonly type = input.required<MessageType>();
	public readonly title = input<string>();
	public readonly dismissId = input<string>();
	public readonly className = input<string>();
	public readonly icon = input<IconType>();
	public readonly hideIcon = input<boolean>(false);
	public readonly button = input<IButton>();

	public readonly noDataImage = computed<IImage | undefined>(() => {
		if (this.type() !== 'noData' && this.type() !== 'noDataOnBody') {
			return undefined;
		}
		return this.appDataService.appContext()?.appSetup?.noDataImage?.[0]?.image;
	});

	public readonly calculatedIcon = computed<IconType | undefined>(() => {
		if (this.hideIcon()) {
			return undefined;
		}

		const icon = this.icon();

		if (icon) {
			return icon;
		}

		return match(this.type())
			.returnType<IconType>()
			.with('info', () => 'info')
			.with('warning', () => 'error')
			.with('error', () => 'error')
			.with('success', () => 'done')
			.with('tip', () => 'idea')
			.otherwise(() => 'info');
	});

	public readonly iconColor = computed<IconColor>(() => {
		return match(this.type())
			.returnType<IconColor>()
			.with('error', () => 'error')
			.with('info', () => 'info')
			.with('success', () => 'success')
			.with('warning', () => 'warning')
			.with('tip', () => 'tip')
			.otherwise(() => 'default');
	});

	public readonly messageDesign = computed<MessageDesign>(() => {
		const title = this.title();

		if (title) {
			return 'combined';
		}

		return 'standalone';
	});

	protected readonly isDismissed = signal<boolean>(false);

	protected readonly isDismissable = computed<boolean>(() => {
		if (!this.dismissId()) {
			return false;
		}
		return true;
	});

	@HostBinding('style') override style = 'display: block';

	constructor() {
		super();

		effect(() => {
			const dismissId = this.dismissId();

			if (dismissId) {
				const dismissedMessages = parseJson<DismissedMessages>(
					this.cookieService.getCookie('DismissedMessages')
				);

				this.isDismissed.set(dismissedMessages?.[dismissId] === '1');
			}
		});
	}

	dismissMessage(): void {
		const dismissId = this.dismissId();

		if (!dismissId) {
			return;
		}

		this.isDismissed.set(true);

		const dismissedMessages = parseJson<DismissedMessages>(this.cookieService.getCookie('DismissedMessages'));

		this.cookieService.setCookie(
			'DismissedMessages',
			stringifyJson<DismissedMessages>({
				...dismissedMessages,
				[dismissId]: '1'
			}),
			{
				expiresInDays: 365
			}
		);
	}
}
